import { useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

interface UseShareActionsOptions {
  shareUrl: string;
  shareText: string;
  listName: string;
}

/**
 * Hook for share actions - haptics, native share, copy link
 */
export function useShareActions({ shareUrl, shareText, listName }: UseShareActionsOptions) {
  const toast = useToast();

  const triggerHaptics = useCallback(async (style: ImpactStyle = ImpactStyle.Light) => {
    try {
      await Haptics.impact({ style });
    } catch {
      // Haptics not available on web
    }
  }, []);

  const handleNativeShare = useCallback(async () => {
    await triggerHaptics();

    // Use Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: listName,
          text: shareText,
          url: shareUrl,
        });
        return true;
      } catch (error) {
        // User cancelled or share failed
        if ((error as Error).name === 'AbortError') {
          return false;
        }
        // Fall through to copy
      }
    }

    // Fallback to copy if Web Share not available
    return handleCopyLink();
  }, [triggerHaptics, listName, shareText, shareUrl]);

  const handleCopyLink = useCallback(async () => {
    await triggerHaptics();

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Link copied!',
        description: 'Share this link with friends.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      return true;
    } catch {
      toast({
        title: 'Failed to copy link',
        status: 'error',
        duration: 2000,
      });
      return false;
    }
  }, [triggerHaptics, shareUrl, toast]);

  return {
    triggerHaptics,
    handleNativeShare,
    handleCopyLink,
  };
}
